'use client';

import { fetchUser, refreshToken, signIn } from '@/lib/auth-client';
import { Amplify } from 'aws-amplify';
import { signOut } from 'aws-amplify/auth';
import { jwtDecode } from 'jwt-decode';
import { useLocale } from 'next-intl';
import { usePathname } from 'next/navigation';
import { createContext, ReactNode, useContext, useLayoutEffect, useState } from 'react';

// Dynamically configure Amplify from environment variables
const amplifyConfig = {
  Auth: {
    Cognito: {
      region: process.env.NEXT_PUBLIC_COGNITO_REGION || '',
      userPoolId: process.env.NEXT_PUBLIC_COGNITO_USERPOOL_ID || '',
      userPoolClientId: process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID || '',
      loginWith: {
        oauth: {
          domain: process.env.NEXT_PUBLIC_COGNITO_DOMAIN || '',
          scopes: ['profile', 'email', 'openid'],
          redirectSignIn: [process.env.NEXT_PUBLIC_COGNITO_REDIRECT_URI || ''],
          redirectSignOut: [process.env.NEXT_PUBLIC_COGNITO_REDIRECT_SIGNOUT_URI || ''],
          responseType: 'code' as const,
        },
      },
    },
  },
};

Amplify.configure(amplifyConfig);

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  user: any | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<boolean>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const pathname = usePathname();
  const locale = useLocale();

  // Add check for public/special routes where the auth check should be skipped
  const isSkippedRoute = ['/avm', '/integration-test', '/auth/callback'].some(route => pathname?.includes(route));

  const [user, setUser] = useState<any | null>(() => {
    if (typeof window !== 'undefined') {
      const savedUser = localStorage.getItem('user');
      return savedUser ? JSON.parse(savedUser) : null;
    }
    return null;
  });

  useLayoutEffect(() => {
    const checkAuth = async () => {
      // First, determine if we should even run the check.
      if (isSkippedRoute) {
        return; // Exit completely, leaving isLoading as true.
      }
      
      // If it's not a skipped route, then we proceed with the full check.
      setError(null);

      try {
        const accessToken = localStorage.getItem('accessToken');
        const userJson = localStorage.getItem('user');

        if (accessToken && userJson) {
          try {
            const userData = JSON.parse(userJson);

            // For OAuth users who might not have roles yet, try to fetch them
            if (!userData.roles || userData.roles.length === 0) {
              try {
                const userResponse = await fetch('/api/auth/session', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    accessToken,
                    userData // Pass existing user data for OAuth users
                  }),
                });

                if (userResponse.ok) {
                  const sessionData = await userResponse.json();
                  if (sessionData.authenticated && sessionData.roles) {
                    userData.roles = sessionData.roles;
                    localStorage.setItem('user', JSON.stringify(userData));
                  }
                }
              } catch (sessionError) {
                console.error('Failed to fetch roles from session:', sessionError);
              }
            }

            if (userData?.email) {
              localStorage.setItem('userEmail', userData.email);
            }
            setUser(userData);
            setIsAuthenticated(true);
          } catch (e) {
            console.error('AuthProvider: Failed to parse user data from localStorage.', e);
            setUser(null);
            setIsAuthenticated(false);
            localStorage.removeItem('user');
            localStorage.removeItem('accessToken');
            localStorage.removeItem('idToken');
            // localStorage.removeItem('refreshToken'); // No longer in localStorage
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
          localStorage.removeItem('user');
        }
      } catch (error: any) {
        console.error('Auth check error:', error);
        setError(error.message || 'Failed to check authentication status');
        setUser(null);
        setIsAuthenticated(false);
        localStorage.removeItem('user');
      } finally {
        // This block now ONLY runs when an auth check has been performed.
        setIsLoading(false);
      }
    };

    checkAuth();

    // Set up a refresh interval (every 10 minutes)
    const refreshInterval = setInterval(async () => {
      const idToken = localStorage.getItem('idToken');
      const userEmail = localStorage.getItem('userEmail'); // Retrieve email

      if (!idToken || !userEmail) {
        console.warn('AuthContext: Skipping token refresh interval check due to missing ID token or user email.');
        return;
      }

      if (idToken) {
        try {
          const decoded = jwtDecode<{ exp: number }>(idToken);
          const currentTime = Math.floor(Date.now() / 1000);
          
          // Refresh if token will expire in the next 15 minutes
          if (decoded.exp - currentTime < 15 * 60) {
            await refreshToken(userEmail); // Pass email to refreshToken
          }
        } catch (error: any) {
          console.error('AuthContext: Token refresh interval error:', error);
          setError(error.message || 'Failed to refresh authentication token');
        }
      }
    }, 10 * 60 * 1000); // 10 minutes

    return () => clearInterval(refreshInterval);
  }, [isSkippedRoute, pathname]);

  const login = async (email: string, password: string) => {
    setError(null);
    try {
      const response = await signIn(email, password);
      
      if (response.success) {
        // Fetch user data after successful login
        const userResponse = await fetchUser();
        
        if (userResponse.success && userResponse.authenticated) {
          const userData = userResponse.user;
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
          if (userData?.email) {
            localStorage.setItem('userEmail', userData.email);
          }
          setIsAuthenticated(true);
          return { success: true };
        } else {
          const errorMessage = userResponse.error || 'Failed to fetch user data';
          setError(errorMessage);
          return { success: false, error: errorMessage };
        }
      } else {
        const errorMessage = response.error || 'Authentication failed';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (error: any) {
      const errorMessage = error.message || 'An unexpected error occurred';
      console.error('Login error:', error);
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    setError(null);
    try {
      // Clear all local storage data.
      // We do NOT update the React state here (e.g., setUser, setIsAuthenticated).
      // Doing so triggers a re-render in ProtectedRoute, causing a race condition
      // with the full-page redirect below.
      localStorage.removeItem('user');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('idToken');
      // localStorage.removeItem('refreshToken'); // No longer in localStorage

      // The key to a federated sign-out is to redirect to the Cognito /logout endpoint.
      // This will handle clearing the Cognito session and then redirecting to the
      // federated provider's logout page, ensuring a complete sign-out.
      if (typeof window !== 'undefined') {
        const cognitoDomain = process.env.NEXT_PUBLIC_COGNITO_DOMAIN;
        const clientId = process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID;
        const baseUrl = process.env.NEXT_PUBLIC_BASE_URL;
        const logoutUri = `${baseUrl}/${locale}/auth/login`;

        if (cognitoDomain && clientId && logoutUri) {
          const cognitoLogoutUrl = `https://${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
          // This full-page redirect is the single source of truth for navigation.
          window.location.href = cognitoLogoutUrl;
        } else {
          console.error('Cognito logout configuration variables are missing. Falling back to local sign-out.');
          await signOut({ global: true });
          window.location.reload(); // Force reload to clear state as a fallback
        }
      }
    } catch (error: any) {
      console.error('Error during sign out:', error);
      // In case of an error, still clear local data and force a reload.
      localStorage.removeItem('user');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('idToken');
      // localStorage.removeItem('refreshToken'); // No longer in localStorage
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    }
  };

  // Manual refresh function
  const refreshAuth = async () => {
    setError(null);
    try {
      const userEmail = localStorage.getItem('userEmail');
      if (!userEmail) {
        throw new Error('No user email found');
      }
      return await refreshToken(userEmail);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to refresh authentication';
      setError(errorMessage);
      return false;
    }
  };

  const clearError = () => {
    setError(null);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        error,
        user,
        login,
        logout,
        refreshAuth,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}